import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";
import ConsultationFormBase from "./ConsultationFormBase";
import LoadingScreen from "../common/LoadingScreen";
import ResponseMessage from "../common/ResponseMessage";
import type { Consultation, PatientWithConstante } from "../../types";

const UpdatePatient: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [consultation, setConsultation] = useState<Consultation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responseMessage, setResponseMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchConsultation = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) throw new Error("No token found");
        const response = await axiosInstance.get(
          API_ENDPOINTS.CONSTANTES.GET_BY_ID(Number(id))
        );
        setConsultation(response.data);
        setLoading(false);
      } catch (err) {
        setError(
          "Erreur lors de la récupération des informations de la consultation"
        );
        setLoading(false);
        console.error("Error fetching consultation:", err);
      }
    };
    if (id) fetchConsultation();
  }, [id]);

  const handleSubmit = async (data: Consultation | PatientWithConstante) => {
    setIsSubmitting(true);
    setResponseMessage("");
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");
      await axiosInstance.put(
        API_ENDPOINTS.CONSTANTES.UPDATE(Number(id)),
        data as Consultation
      );
      setResponseMessage("Consultation mise à jour avec succès !");
      setTimeout(() => navigate("/admin/dashboard/patient"), 1500);
    } catch (err) {
      setResponseMessage("Erreur lors de la mise à jour de la consultation");
      console.error("Error updating consultation:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading)
    return (
      <LoadingScreen
        message="Chargement des données de la consultation..."
        role="admin"
      />
    );
  if (error || !consultation)
    return (
      <ResponseMessage
        message={error || "Consultation non trouvée"}
        role="admin"
      />
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 p-4">
      <ConsultationFormBase
        mode="update"
        initialData={consultation}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        role="admin"
      />
      {responseMessage && (
        <ResponseMessage message={responseMessage} role="admin" />
      )}
    </div>
  );
};

export default UpdatePatient;
